import { FormEvent, useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { LOGIN_VIEWPORT_QUERY, loginResponseMessage } from '@/lib/loginPath';
import { resolvePostLoginTarget } from '@/lib/mobileAdminPaths';
import { useSession } from '@/hooks/useSession';
import { AppVersionLabel } from '@/components/AppVersionLabel';
import { APP_BRAND_TITLE } from '@/lib/appVersion';
import { Button } from '@/components/ui/Button';
import { inputClass, labelClass } from '@/components/ui/inputStyles';

type LoginResp = { ok: boolean; err?: string; detail?: string };

export function LoginPage() {
  const { ok, loading } = useSession();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionExpired = searchParams.get('session') === 'expired';
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [err, setErr] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia(LOGIN_VIEWPORT_QUERY);
    const goMobile = () => {
      navigate(`/m/login${window.location.search}`, { replace: true });
    };
    if (mq.matches) {
      goMobile();
      return;
    }
    const onChange = () => {
      if (mq.matches) goMobile();
    };
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [navigate]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setErr('');
    setSubmitting(true);
    try {
      const r = await apiFetch<LoginResp>('login', {
        method: 'POST',
        body: JSON.stringify({ username, password }),
      });
      if (r.ok) {
        navigate(resolvePostLoginTarget(searchParams), { replace: true });
      } else {
        const msg =
          r.err === 'account_expired' && r.detail ? r.detail : loginResponseMessage(r.err);
        setErr(msg);
      }
    } catch {
      setErr('网络错误，请确认 PHP 已启动且可通过 /api/api.php 访问');
    } finally {
      setSubmitting(false);
    }
  }

  if (!loading && ok) {
    return <Navigate to={resolvePostLoginTarget(searchParams)} replace />;
  }

  return (
    <div
      className="relative flex min-h-screen items-center justify-center overflow-hidden bg-app-mesh px-4 py-10"
      style={{
        paddingTop: 'max(2.5rem, env(safe-area-inset-top))',
        paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom))',
      }}
    >
      <div
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_70%_45%_at_50%_-15%,rgba(45,212,191,0.12),transparent)]"
        aria-hidden
      />
      <div className="relative w-full max-w-[440px] rounded-3xl border border-slate-200/70 bg-white/95 p-8 shadow-card shadow-slate-900/10 backdrop-blur-sm">
        <div className="mb-6 flex items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-icon text-2xl text-white shadow-lg shadow-teal-900/25 ring-1 ring-white/15">
            <i className="fa-solid fa-shrimp" aria-hidden />
          </span>
          <div className="min-w-0 pt-0.5">
            <h1 className="text-xl font-semibold tracking-tight text-slate-900">登录{APP_BRAND_TITLE}</h1>
            <p className="mt-1 text-sm leading-relaxed text-slate-600">本页适合电脑浏览器。</p>
          </div>
        </div>
        <form onSubmit={onSubmit} className="space-y-5">
          {sessionExpired ? (
            <p className="rounded-xl border border-teal-200/90 bg-teal-50/95 px-3.5 py-2.5 text-sm text-teal-950 shadow-sm">
              登录已过期，请重新登录后继续操作。
            </p>
          ) : null}
          <div>
            <label className={labelClass} htmlFor="login-user">
              用户名
            </label>
            <input
              id="login-user"
              className={inputClass}
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
            />
          </div>
          <div>
            <label className={labelClass} htmlFor="login-pass">
              密码
            </label>
            <input
              id="login-pass"
              type="password"
              className={inputClass}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
            />
          </div>
          {err ? <p className="text-sm text-rose-600">{err}</p> : null}
          <Button type="submit" variant="primary" className="w-full" disabled={submitting}>
            {submitting ? '登录中…' : '登录'}
          </Button>
          <p className="text-center text-xs leading-relaxed text-slate-500">
            <Link to="/" className="font-medium text-slate-600 underline decoration-slate-300 underline-offset-2 hover:text-slate-900">
              了解产品
            </Link>
            <span className="mx-1.5 text-slate-300">·</span>
            <Link to="/m/login" className="font-medium text-teal-700 underline decoration-teal-600/30 underline-offset-2 hover:text-teal-800">
              手机版登录
            </Link>
          </p>
          <p className="mt-4 text-center">
            <AppVersionLabel />
          </p>
        </form>
      </div>
    </div>
  );
}
